import axios from "axios";
import { format } from "date-fns";
// import "context-filter-polyfill";
// this function uses different routes for client and operator
import { prepareUploadLargeMOTGImage } from "../axios/routes/moment";
import { MomentResponse } from "../shared/interfaces/moment";
import { IntervalGamePopulated } from "../shared/interfaces";
import {
  SCOREBOARD_DIVIDER_THICKNESS_CQH_FRAC,
  SCOREBOARD_LOGO_DOWNLOAD_MULT,
  SCOREBOARD_MOMENT_TITLE_FONT_PX,
  SCOREBOARD_MOMENT_TITLE_FONT_MIN_PX_UI,
  SCOREBOARD_MOMENT_TITLE_RESERVE_CHARS,
  SCOREBOARD_MOBILE_DOWNLOAD_SCORE_BOOST,
  SCOREBOARD_MOBILE_DOWNLOAD_STADIUM_BOOST,
  SCOREBOARD_RASTER_HEADER_COPY_EXTRA_MULT,
  SCOREBOARD_SCORE_FONT_MIN_PX_UI,
  SCOREBOARD_SCORE_FONT_PX,
  SCOREBOARD_SHAPE_DIVIDER_GAP_PX,
  SCOREBOARD_STADIUM_FONT_MAX_PX_UI,
  SCOREBOARD_STADIUM_FONT_MIN_PX_UI,
  SCOREBOARD_STADIUM_FONT_PX,
  SCOREBOARD_TRAP_SKEW_FRAC,
  computeScoreboardOverlayTypography,
} from "./scoreboardConstants";
import { isMobileCanvasExport } from "./device";

// TODO: use the same functions both in client and in client-operator

/** Ticket template date field, e.g. JUNE 6TH, 2026 */
export function formatTicketGameDate(startDateMs: number): string {
  const d = new Date(startDateMs);
  if (!Number.isFinite(d.getTime())) return "";
  return `${format(d, "MMMM").toUpperCase()} ${format(d, "do").toUpperCase()}, ${format(d, "yyyy")}`;
}

export enum ASPECT_RATIO_LIST {
  "9_16" = 9 / 16,
  "3_4" = 3 / 4,
  "6_4" = 6 / 4,
  "16_9" = 16 / 9,
  "1_1" = 1,
  "8_10" = 8 / 10,
}

/** Minimum canvas width for sponsor overlay rendering to avoid pixelation when zoomed */
export const MIN_SPONSOR_RENDER_WIDTH = 1600;

/**
 * Floor export width when painting {@link renderMomentHeader} so score/caption scale like desktop.
 * Sponsor-only exports keep {@link MIN_SPONSOR_RENDER_WIDTH}.
 */
export const MIN_MOMENT_HEADER_EXPORT_WIDTH = 2048;

export function minMomentCanvasExportWidth(hasMomentHeader: boolean): number {
  return hasMomentHeader
    ? Math.max(MIN_SPONSOR_RENDER_WIDTH, MIN_MOMENT_HEADER_EXPORT_WIDTH)
    : MIN_SPONSOR_RENDER_WIDTH;
}

/** PNG/GIF raster export on phones / in-app WebViews — boost trap score numerals only (desktop unchanged). */
export function momentHeaderDownloadScoreBoost(hasHeader: boolean): number {
  return hasHeader && isMobileCanvasExport()
    ? SCOREBOARD_MOBILE_DOWNLOAD_SCORE_BOOST
    : 1;
}

/** PNG/GIF raster export on phones / in-app WebViews — boost stadium name (same WebKit fix as scores). */
export function momentHeaderDownloadStadiumBoost(hasHeader: boolean): number {
  return hasHeader && isMobileCanvasExport()
    ? SCOREBOARD_MOBILE_DOWNLOAD_STADIUM_BOOST
    : 1;
}

export interface ImageCoordinates {
  x: number;
  y: number;
}

export interface ImageSize {
  width: number;
  height: number;
}

export type EditorFilter = "none" | "grayscale" | "sepia" | "vintage";

export const EDITOR_FILTER_CSS: Record<EditorFilter, string> = {
  none: "none",
  grayscale: "grayscale(100%)",
  sepia: "sepia(80%)",
  vintage: "sepia(40%) contrast(1.1) saturate(0.8)",
};

export interface CustomAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
}

export const DEFAULT_CUSTOM_ADJUSTMENTS: CustomAdjustments = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
};

export const buildImageFilterCss = (
  filter?: EditorFilter,
  custom?: Partial<CustomAdjustments>,
): string => {
  const parts: string[] = [];
  if (filter && filter !== "none") parts.push(EDITOR_FILTER_CSS[filter]);
  const adj = { ...DEFAULT_CUSTOM_ADJUSTMENTS, ...custom };
  parts.push(`brightness(${adj.brightness})`);
  parts.push(`contrast(${adj.contrast})`);
  parts.push(`saturate(${adj.saturation})`);
  return parts.join(" ");
};

export interface ImageCropData extends ImageCoordinates, ImageSize {
  aspect?: ASPECT_RATIO_LIST;
  filter?: EditorFilter;
  customAdjustments?: Partial<CustomAdjustments>;
  /** When true, x/y/width/height are 0-1 ratios; multiply by image dimensions for pixel coords */
  normalized?: boolean;
  /**
   * Reserved seats: true = crop rect is in getGameMoments image pixels (API width/height);
   * false/undefined after editor save = bitmap / natural pixels from Cropper.
   */
  momentApiFrameCrop?: boolean;
}

/**
 * Largest portrait crop of `aspect` inside the image, centered (bitmap / natural px).
 * Used when there is no saved crop and no API center (e.g. after navigation before refetch).
 */
export function centeredAspectCropInImage(
  naturalWidth: number,
  naturalHeight: number,
  aspect: number = ASPECT_RATIO_LIST["8_10"],
): ImageCropData {
  if (!naturalWidth || !naturalHeight) {
    return { x: 0, y: 0, width: 400, height: 500 };
  }
  const iw = naturalWidth;
  const ih = naturalHeight;
  if (iw / ih > aspect) {
    const sh = ih;
    const sw = ih * aspect;
    return { x: (iw - sw) / 2, y: 0, width: sw, height: sh };
  }
  const sw = iw;
  const sh = sw / aspect;
  return { x: 0, y: (ih - sh) / 2, width: sw, height: sh };
}

export const calculateImageStartPosition = (
  cropData: ImageCropData | null,
  centerCoordsX: number,
  centerCoordsY: number,
  defaultZoomWidth: number,
  defaultZoomHeight: number,
  offsetTop: number,
  horizontalOffset: number,
): ImageCoordinates => {
  if (cropData) {
    return {
      x: cropData.x,
      y: cropData.y,
    };
  }

  return {
    x: centerCoordsX - defaultZoomWidth / 2 + horizontalOffset,
    y: centerCoordsY - defaultZoomHeight / 2 - offsetTop,
  };
};

export const calculateImageSize = (
  cropData: ImageCropData | null,
  defaultZoomWidth: number,
  defaultZoomHeight: number,
): ImageSize => {
  if (cropData) {
    return {
      width: cropData.width,
      height: cropData.height,
    };
  }

  return {
    width: defaultZoomWidth,
    height: defaultZoomHeight,
  };
};

export interface MomentHeaderData {
  moment?: MomentResponse;
  selectedGameData?: IntervalGamePopulated;
  team1LogoImage?: HTMLImageElement;
  team2LogoImage?: HTMLImageElement;
  logoImage?: HTMLImageElement;
  team1Color?: string;
  team2Color?: string;
  team1SecondaryColor?: string;
  team2SecondaryColor?: string;

  minWidth?: number; // for downloading
  maxWidth?: number; // for rendering
  team1SponsorLogoImage?: HTMLImageElement;
  hideScoreboard?: boolean; // allow to hide scoreboard. It overwrites moment's flag
  hideCaption?: boolean;
  caption?: string;
  /** Home team operator: optional scoreboard strip background (#RGB / #RRGGBB). */
  scoreboardBackgroundColor?: string;
  /** Home team operator: optional moment title (jersey) color. */
  scoreboardDescriptionColor?: string;
}

/** Cap decoded logo bitmaps — header displays ~48px; full-res canvases OOM mobile Safari. */
const MAX_DATAURL_IMAGE_PX = 384;

// try to reduce canvas memory: Safari issue with 384Mb for all canvas
export const releaseCanvas = (canvas: HTMLCanvasElement): void => {
  if (canvas) {
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext("2d");
    if (context) {
      context.clearRect(0, 0, 1, 1);
    }
  }
};

/**
 * Render overlay over image
 * @param context - canvas context to render on
 * @param sponsorImage - overlay image
 * @param canvasXOffset - x offset
 * @param canvasYOffset - y offset
 * @param canvasWidth - area width
 * @param canvasHeight - area height
 */
export const renderSponsorOverlay = (
  context: CanvasRenderingContext2D,
  sponsorImage: HTMLImageElement,
  canvasXOffset: number,
  canvasYOffset: number,
  canvasWidth: number,
  canvasHeight: number,
): void => {
  const width = canvasWidth; //  - canvasXOffset * 2;
  const height = canvasHeight; //  - canvasYOffset * 2;
  const sponsorImageAspect = sponsorImage.height / sponsorImage.width;

  let newWidth = canvasWidth;
  let newHeight = newWidth * sponsorImageAspect;
  if (newHeight > height) {
    const ratio = height / newHeight;
    newHeight *= ratio;
    newWidth *= ratio;
  }

  const newX = canvasXOffset + (width - newWidth) / 2.0;

  if (sponsorImage.width > sponsorImage.height) {
    const newY = canvasYOffset + height - newHeight;

    context.drawImage(
      sponsorImage,
      newX,
      newY,
      newWidth,
      newHeight,
    );

    return;
  }

  // cut overlay on 2 parts and draw them aligned to top/bottom borders
  const halfHeight = newHeight / 2.0;
  context.drawImage(
    sponsorImage,
    0,
    0,
    sponsorImage.width,
    sponsorImage.height / 2.0,
    newX,
    canvasYOffset,
    newWidth,
    halfHeight,
  );

  context.drawImage(
    sponsorImage,
    0,
    sponsorImage.height / 2.0,
    sponsorImage.width,
    sponsorImage.height / 2.0,
    newX,
    canvasYOffset + height - halfHeight,
    newWidth,
    halfHeight,
  );
};

export const COLORS_PARTIAL = {
  WHITE: "#FFFFFF",
  BLACK: "#000000",
  GREY_DARK: "#252847",
  GREEN_DARK: "#02653B",
};

export enum FontFamilyTypeEnum_PARTIAL {
  POPPINS = "Poppins",
  Copperplate = "Copperplate",
  Seahawks = "Seahawks",
  SFPRO = "SFPRO",
  Mariners = "Mariners",
  Kraken = "Kraken",
  SITickets = "SITickets",
  MONTSERRAT = "Montserrat",
  APEX = "Conv_Apex Mk3-Medium",
  OSWALD = "Oswald",
  OpinionPro = "OpinionPro",
  OpinionProCondensed = "OpinionProCondensed",
  OpinionProExtraCondensed = "OpinionProExtraCondensed",
  Roboto = "Roboto",
  SackersGothic = "SackersGothic",
}

/** Canvas: same family order as FONT_STACK_SCOREBOARD_JERSEY in components/styled.ts. */
const CANVAS_JERSEY_FONT_STACK = "'Jersey M54', 'Jersey 15', Arial, sans-serif";

/** Canvas stadium row — matches {@link ScoreboardStadium} (Montserrat 600) in styled.ts. */
const CANVAS_STADIUM_FONT_STACK = "'Montserrat', Arial, sans-serif";

/** My Moments HTML max stadium/score ratio — derive export px from finalized trap score. */
const STADIUM_TO_SCORE_EXPORT_RATIO = (
  SCOREBOARD_STADIUM_FONT_MAX_PX_UI / SCOREBOARD_SCORE_FONT_PX
);

/**
 * Mobile raster: size stadium from finalized trap score (scores render correctly on WebKit).
 * Falls back to overlay ref scaling when scoreFontPx is unavailable.
 */
function computeStadiumExportFontPx(
  innerH: number,
  titlePx: number,
  overlayScorePx: number,
  isRasterExport: boolean,
  scaleOverlayPx: (overlayPx: number, titlePxArg: number) => number,
  overlayStadiumPx: number,
  scoreFontPx?: number,
  stadiumBoost = 1,
): number {
  if (isRasterExport && scoreFontPx && scoreFontPx > 0) {
    return Math.min(
      Math.floor(innerH * 0.72),
      Math.max(
        18,
        Math.round(scoreFontPx * STADIUM_TO_SCORE_EXPORT_RATIO * stadiumBoost),
      ),
    );
  }
  if (isRasterExport && isMobileCanvasExport()) {
    const stadiumOverlayRef = (
      overlayScorePx * SCOREBOARD_STADIUM_FONT_PX
    ) / SCOREBOARD_MOMENT_TITLE_FONT_PX;
    return Math.min(
      Math.floor(innerH * 0.72),
      Math.max(18, Math.round(scaleOverlayPx(stadiumOverlayRef, titlePx) * stadiumBoost)),
    );
  }
  if (isRasterExport) {
    return Math.min(
      Math.floor(innerH * 0.4),
      Math.max(12, scaleOverlayPx(SCOREBOARD_STADIUM_FONT_PX, titlePx)),
    );
  }
  return Math.max(
    SCOREBOARD_STADIUM_FONT_MIN_PX_UI,
    scaleOverlayPx(overlayStadiumPx, titlePx),
  );
}

/**
 * iOS WebKit often ignores canvas fillText font-size for sub-row labels — paint to an offscreen
 * bitmap at the target px size, then drawImage onto the export canvas (same trap score workaround).
 */
function drawStadiumTextOnCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  fontPx: number,
  fillStyle: string,
  useBitmap: boolean,
): number {
  const px = Math.max(10, Math.round(fontPx));
  const fontCss = `600 ${px}px ${CANVAS_STADIUM_FONT_STACK}`;
  ctx.font = fontCss;
  const textW = ctx.measureText(text).width;

  if (useBitmap && typeof document !== "undefined") {
    const padX = Math.ceil(px * 0.4);
    const padY = Math.ceil(px * 0.25);
    const offW = Math.max(1, Math.ceil(textW + padX * 2));
    const offH = Math.max(1, Math.ceil(px * 1.3 + padY * 2));
    const off = document.createElement("canvas");
    off.width = offW;
    off.height = offH;
    const octx = off.getContext("2d");
    if (octx) {
      octx.font = fontCss;
      octx.textAlign = "center";
      octx.textBaseline = "middle";
      octx.fillStyle = fillStyle;
      octx.fillText(text, offW / 2, offH / 2);
      ctx.drawImage(off, centerX - offW / 2, centerY - offH / 2, offW, offH);
      releaseCanvas(off);
      return textW;
    }
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = fillStyle;
  ctx.font = fontCss;
  ctx.fillText(text, centerX, centerY);
  return textW;
}

const renderRoundedBox = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  color: string,
) => {
  context.beginPath();
  context.fillStyle = color;
  context.strokeStyle = color;
  context.moveTo(x, y + r);
  context.lineTo(x, y + h - r);
  context.arcTo(x, y + h, x + r, y + h, r);
  context.lineTo(x + w - r, y + h);
  context.arcTo(x + w, y + h, x + w, y + h - r, r);
  context.lineTo(x + w, y + r);
  context.arcTo(x + w, y, x + w - r, y, r);
  context.lineTo(x + r, y);
  context.arcTo(x, y, x, y + r, r);
  context.stroke();
  context.fill();
  context.closePath();
};

/** Trapezoid score panel: one vertical outer edge; inner edge angled toward center (spec). */
const fillTrapezoidScorePanel = (
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  width: number,
  height: number,
  skew: number,
  fill: string,
  variant: "leading" | "trailing",
): void => {
  ctx.beginPath();
  if (variant === "leading") {
    ctx.moveTo(left, top);
    ctx.lineTo(left + width, top);
    ctx.lineTo(left + width - skew, top + height);
    ctx.lineTo(left, top + height);
  } else {
    ctx.moveTo(left, top);
    ctx.lineTo(left + width, top);
    ctx.lineTo(left + width, top + height);
    ctx.lineTo(left + skew, top + height);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
};

/** Accent line parallel to inner slant (team 2 secondary leading, team 1 secondary trailing). */
const strokeSlantDividerLine = (
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  width: number,
  height: number,
  skew: number,
  variant: "leading" | "trailing",
  lineWidth: number,
  strokeColor: string,
  gapPx: number,
): void => {
  ctx.save();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "butt";
  ctx.beginPath();
  /* Full slant length; extend slightly so butt caps are not clipped at panel edges (matches SVG overlay). */
  const edgeIn = 0;
  const extendPx = lineWidth * 0.55;
  const len = Math.hypot(skew, height);
  const nx = height / len;
  const ny = skew / len;
  if (variant === "leading") {
    const ox = gapPx * nx;
    const oy = gapPx * ny;
    const xb = left + width - skew - edgeIn + ox;
    const yb = top + height + oy;
    const xt = left + width - edgeIn + ox;
    const yt = top + oy;
    const uxb = skew / len;
    const uyb = -height / len;
    ctx.moveTo(xb - uxb * extendPx, yb - uyb * extendPx);
    ctx.lineTo(xt + uxb * extendPx, yt + uyb * extendPx);
  } else {
    const ox = -gapPx * nx;
    const oy = gapPx * ny;
    const xt = left + edgeIn + ox;
    const yt = top + oy;
    const xb = left + skew + edgeIn + ox;
    const yb = top + height + oy;
    const uxb = skew / len;
    const uyb = height / len;
    ctx.moveTo(xt - uxb * extendPx, yt - uyb * extendPx);
    ctx.lineTo(xb + uxb * extendPx, yb + uyb * extendPx);
  }
  ctx.stroke();
  ctx.restore();
};

/** Fixed banner aspect: scale = bannerWidth/400 preserves proportions, no stretching */
const BANNER_REF_WIDTH = 400;
/** Prevent the scoreboard strip from consuming the whole frame on very wide exports. */
const SCOREBOARD_CANVAS_SCALE_CAP = 2.35;
/** Never shrink header layout below this vs 400px ref (keeps type/logos readable on small crops). */
const SCOREBOARD_CANVAS_SCALE_FLOOR = 0.95;
/** Minimum logo slot edge (px) after scaling — avoids microscopic marks on download. */
const SCOREBOARD_LOGO_SLOT_MIN_PX = 42;

/** PNG / full-quality GIF raster: enlarge header band, logos, and center copy vertically vs preview */
export const SCOREBOARD_RASTER_VERTICAL_BOOST = 1.25;

export function scoreboardCanvasScale(bannerWidth: number): number {
  const rawScale = bannerWidth / BANNER_REF_WIDTH;
  return Math.min(Math.max(rawScale, SCOREBOARD_CANVAS_SCALE_FLOOR), SCOREBOARD_CANVAS_SCALE_CAP);
}

/** Band metrics shared by raster render + font preload (matches {@link renderMomentHeader} layout). */
export function scoreboardHeaderBandMetrics(
  bannerWidth: number,
  downloadVerticalBoost = 1,
): { scale: number; innerH: number; copyScale: number; isRasterExport: boolean } {
  const scale = scoreboardCanvasScale(bannerWidth);
  const vBoost = downloadVerticalBoost;
  const isRasterExport = vBoost > 1;
  const rasterCopyExtra = isRasterExport ? SCOREBOARD_RASTER_HEADER_COPY_EXTRA_MULT : 1;
  const copyScale = scale * vBoost * rasterCopyExtra;
  const padY = Math.max(1, 1.6 * scale * vBoost);
  const bandH = 58 * scale * vBoost;
  const innerH = Math.max(18 * scale * vBoost, bandH - 2 * padY);
  return { scale, innerH, copyScale, isRasterExport };
}

/** Preload Jersey at the exact px sizes canvas will paint (WebKit export skips fonts otherwise). */
export async function preloadMomentHeaderFonts(
  bannerWidth: number,
  downloadVerticalBoost = 1,
): Promise<void> {
  if (typeof document === "undefined" || !document.fonts?.load) return;

  const { innerH, copyScale, isRasterExport } = scoreboardHeaderBandMetrics(
    bannerWidth,
    downloadVerticalBoost,
  );
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 0;
  const overlayTypo = computeScoreboardOverlayTypography(bannerWidth, innerH, viewportW);
  const titlePx = isRasterExport
    ? Math.round(SCOREBOARD_MOMENT_TITLE_FONT_PX * copyScale)
    : overlayTypo.titlePx;
  const titleToOverlayRatio = overlayTypo.titlePx > 0 ? titlePx / overlayTypo.titlePx : 1;
  const overlayTitlePxForPreload = Math.max(overlayTypo.titlePx, 1);
  const scorePx = Math.max(
    12,
    Math.min(Math.floor(innerH * 0.72), Math.round(overlayTypo.scorePx * titleToOverlayRatio)),
  );
  const scaleOverlayPxForPreload = (overlayPx: number, titlePxArg: number): number => (
    Math.round((titlePxArg / overlayTitlePxForPreload) * overlayPx)
  );
  const stadiumPx = computeStadiumExportFontPx(
    innerH,
    titlePx,
    overlayTypo.scorePx,
    isRasterExport,
    scaleOverlayPxForPreload,
    overlayTypo.stadiumPx,
    scorePx,
    momentHeaderDownloadStadiumBoost(true),
  );

  await Promise.race([
    Promise.all([
      document.fonts.load(`700 ${titlePx}px 'Jersey M54'`),
      document.fonts.load(`600 ${stadiumPx}px 'Montserrat'`),
      document.fonts.load(`700 ${scorePx}px 'Jersey M54'`),
      document.fonts.load(`700 ${titlePx}px 'Jersey 15'`),
      document.fonts.load(`700 ${scorePx}px 'Jersey 15'`),
    ]),
    new Promise<void>((r) => window.setTimeout(r, 2800)),
  ]);
  if (document.fonts?.ready) {
    await Promise.race([
      document.fonts.ready,
      new Promise<void>((r) => window.setTimeout(r, 900)),
    ]);
  }
}

/**
 * Fan moment scoreboard (HTML overlay + downloads): Team2 skew score | T2 logo | center copy
 * (moment title in T2 primary, stadium in T1 primary with T1-primary flanking rules)
 * | T1 logo | T1 skew score.
 * Outer border = Team 1 secondary. Matches {@link MomentoCanvas} overlay spec.
 *
 * @param downloadVerticalBoost Raster export: strip + type scale (e.g. {@link SCOREBOARD_RASTER_VERTICAL_BOOST}).
 * @param logoSlotScale Multiplier on team logo slot size only (e.g. {@link SCOREBOARD_LOGO_DOWNLOAD_MULT} on PNG).
 * @param downloadScoreBoost Mobile PNG/GIF export only — enlarges trap score numerals, not title/stadium/logos.
 */
export const renderMomentHeader = (
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  headerData: MomentHeaderData,
  scaleToImageSize?: boolean,
  skipLogos = false,
  downloadVerticalBoost = 1,
  logoSlotScale = 1,
  _downloadScoreBoost = 1,
): void => {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  const bannerWidth = canvasWidth;
  const bannerLeft = 0;
  const scale = scoreboardCanvasScale(bannerWidth);
  const vBoost = downloadVerticalBoost;
  const rasterCopyExtra = vBoost > 1 ? SCOREBOARD_RASTER_HEADER_COPY_EXTRA_MULT : 1;
  const copyScale = scale * vBoost * rasterCopyExtra;
  const isRasterExport = vBoost > 1;

  const t1p = (
    headerData.team1Color?.trim()
    || headerData.selectedGameData?.team1?.primaryColor?.trim()
    || "#003263"
  );
  const t1s = (
    headerData.team1SecondaryColor?.trim()
    || headerData.selectedGameData?.team1?.secondaryColor?.trim()
    || "#252847"
  );
  const t2p = (
    headerData.team2Color?.trim()
    || headerData.selectedGameData?.team2?.primaryColor?.trim()
    || "#0A7CB2"
  );
  const t2s = (
    headerData.team2SecondaryColor?.trim()
    || headerData.selectedGameData?.team2?.secondaryColor?.trim()
    || t2p
  );
  const momentTitleColor = (
    // Fall back to the home team's primary color (already reflects any
    // game-level or weekday secondary-primary-color override applied server-side).
    headerData.scoreboardDescriptionColor?.trim()
    || t1p
  );
  const scoreboardBandBackground = headerData.scoreboardBackgroundColor?.trim() || COLORS_PARTIAL.WHITE;

  const sport = headerData.selectedGameData?.sportName;
  const isOtherSport = !!(sport && sport.startsWith("Other"));
  const headerText = (headerData.moment?.text || headerData.caption || "").trim();
  const stadiumLabel = (headerData.selectedGameData?.stadium || headerData.moment?.stadium || "").trim();
  const titleUpper = headerText ? headerText.toUpperCase() : "";
  const hasMomentScores = (
    !isOtherSport
    && headerData.moment?.team1Score != null
    && headerData.moment?.team2Score != null
  );
  const includeTeamScores = headerData.selectedGameData?.team1?.momentScoreboardIncludeTeamScores !== false;
  const showScoreboardRow = (
    !headerData.hideScoreboard
    && !isOtherSport
    && hasMomentScores
  );
  const showLogoScoreboardRow = (
    !headerData.hideScoreboard
    && !isOtherSport
    && (hasMomentScores || !!(titleUpper || stadiumLabel))
  );
  const hasCenterCopy = !!(titleUpper || stadiumLabel);

  if (!showLogoScoreboardRow) {
    return;
  }
  if (showScoreboardRow && !headerData.moment) {
    return;
  }

  const bandH = (showLogoScoreboardRow ? 58 * scale : 42 * scale) * vBoost;
  const totalHeight = bandH;
  const x = bannerLeft;
  const y = 0;

  context.beginPath();
  context.rect(x, y, bannerWidth, totalHeight);
  context.closePath();
  context.fillStyle = scoreboardBandBackground;
  context.fill();
  context.strokeStyle = t1s;
  context.lineWidth = Math.max(1.5, 2 * scale * vBoost);
  context.stroke();

  const padY = Math.max(1, 1.6 * scale * vBoost);
  const padX = Math.max(0.85, 1.48 * scale);
  const innerTop = y + padY;
  const innerH = Math.max(18 * scale * vBoost, totalHeight - 2 * padY);
  /** Same clamp formulas as My Moments HTML overlay ({@link FanMomentScoreboardBar}). */
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 0;
  const overlayTypo = computeScoreboardOverlayTypography(bannerWidth, innerH, viewportW);
  const titleStartPx = isRasterExport
    ? Math.round(SCOREBOARD_MOMENT_TITLE_FONT_PX * copyScale)
    : overlayTypo.titlePx;
  const titleFloorPx = isRasterExport
    ? Math.max(10, Math.round(11 * copyScale))
    : SCOREBOARD_MOMENT_TITLE_FONT_MIN_PX_UI;
  const overlayTitlePx = Math.max(overlayTypo.titlePx, 1);
  const scaleOverlayPx = (overlayPx: number, titlePxArg: number): number => (
    Math.round((titlePxArg / overlayTitlePx) * overlayPx)
  );
  const innerLeft = x + padX;
  const innerRight = x + bannerWidth - padX;
  const innerW = Math.max(0, innerRight - innerLeft);
  const gap = Math.max(2, 2.5 * scale);
  const midY = innerTop + innerH / 2;
  const shapeDividerGapPx = SCOREBOARD_SHAPE_DIVIDER_GAP_PX * scale * vBoost;
  const dividerLane = Math.max(
    0.03,
    SCOREBOARD_DIVIDER_THICKNESS_CQH_FRAC * innerH * 0.4 + 0.03 * scale + shapeDividerGapPx,
  );

  const drawLogoInSlot = (img: HTMLImageElement | undefined, slotLeft: number, slotSize: number) => {
    if (!img?.width) return;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    if (!imgW || !imgH) return;
    const aspect = imgW / imgH;
    let drawW: number;
    let drawH: number;
    if (aspect >= 1) {
      drawW = slotSize;
      drawH = slotSize / aspect;
    } else {
      drawH = slotSize;
      drawW = slotSize * aspect;
    }
    const drawX = slotLeft + (slotSize - drawW) / 2;
    const drawY = midY - drawH / 2;
    context.drawImage(img, drawX, drawY, drawW, drawH);
  };

  const stadiumBoost = isRasterExport ? momentHeaderDownloadStadiumBoost(true) : 1;
  const useBitmapStadium = isRasterExport && isMobileCanvasExport();

  const drawCenterCopyBlock = (
    blockLeft: number,
    blockW: number,
    presetStadiumFontPx?: number,
    scoreFontPx?: number,
  ) => {
    let titleSize = 0;
    context.save();
    try {
      /* Slightly below upper third so the title is not cramped against the band edge */
      const ty = innerTop + innerH * 0.36;
      if (titleUpper) {
        titleSize = titleStartPx;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillStyle = momentTitleColor;
        context.font = `700 ${titleSize}px ${CANVAS_JERSEY_FONT_STACK}`;
        let tw = context.measureText(titleUpper).width;
        while (tw > blockW * 0.9 && titleSize > titleFloorPx) {
          titleSize -= 1;
          context.font = `700 ${titleSize}px ${CANVAS_JERSEY_FONT_STACK}`;
          tw = context.measureText(titleUpper).width;
        }
        context.fillText(titleUpper, blockLeft + blockW / 2, ty);
      }
    } finally {
      context.restore();
    }

    if (!stadiumLabel) {
      return;
    }

    let stSize = presetStadiumFontPx ?? computeStadiumExportFontPx(
      innerH,
      titleUpper ? titleSize : titleStartPx,
      overlayTypo.scorePx,
      isRasterExport,
      scaleOverlayPx,
      overlayTypo.stadiumPx,
      scoreFontPx,
      stadiumBoost,
    );
    const stUpper = stadiumLabel.toUpperCase();
    const stadiumFontCss = `600 ${stSize}px ${CANVAS_STADIUM_FONT_STACK}`;
    context.font = stadiumFontCss;
    let sw = context.measureText(stUpper).width;
    if (!isRasterExport) {
      const stadiumFloorPx = Math.max(
        SCOREBOARD_STADIUM_FONT_MIN_PX_UI,
        Math.round(stSize * 0.85),
      );
      while (sw > blockW * 0.92 && stSize > Math.max(8, stadiumFloorPx - 2)) {
        stSize -= 1;
        context.font = `600 ${stSize}px ${CANVAS_STADIUM_FONT_STACK}`;
        sw = context.measureText(stUpper).width;
      }
      stSize = Math.max(
        Math.min(stSize, Math.floor(innerH * 0.48)),
        Math.min(stadiumFloorPx, Math.floor(innerH * 0.45)),
      );
    } else if (presetStadiumFontPx != null || scoreFontPx) {
      stSize = Math.max(
        Math.min(stSize, Math.floor(innerH * 0.72)),
        18,
      );
      context.font = `600 ${stSize}px ${CANVAS_STADIUM_FONT_STACK}`;
      sw = context.measureText(stUpper).width;
    }
    const stadiumY = titleUpper ? innerTop + innerH * 0.76 : midY;
    const lineH = Math.max(0.75, 1 * scale * vBoost);
    const gapCh = 5 * scale * vBoost;
    const maxSide = Math.min(blockW * 0.28, (blockW - sw - 2 * gapCh) / 2);
    const sideLineW = Math.max(10 * scale * vBoost, maxSide);
    const rowW = 2 * sideLineW + 2 * gapCh + sw;
    const row0 = blockLeft + (blockW - rowW) / 2;
    const textCenterX = row0 + sideLineW + gapCh + sw / 2;
    context.fillStyle = t1p;
    context.fillRect(row0, stadiumY - lineH / 2, sideLineW, lineH);
    drawStadiumTextOnCanvas(
      context,
      stUpper,
      textCenterX,
      stadiumY,
      stSize,
      t1p,
      useBitmapStadium,
    );
    context.fillStyle = t1p;
    context.fillRect(row0 + sideLineW + gapCh + sw + gapCh, stadiumY - lineH / 2, sideLineW, lineH);
  };

  if (showScoreboardRow) {
    const showScoreNumbers = includeTeamScores;
    const team2Score = String(headerData.moment?.team2Score);
    const team1Score = String(headerData.moment?.team1Score);
    const homeOnTop = headerData.selectedGameData?.team1?.homeTeamOnTop === true;
    const leftScore = homeOnTop ? team1Score : team2Score;
    const rightScore = homeOnTop ? team2Score : team1Score;
    const leftP = homeOnTop ? t1p : t2p;
    const leftS = homeOnTop ? t1s : t2s;
    const rightP = homeOnTop ? t2p : t1p;
    const rightS = homeOnTop ? t2s : t1s;
    const leftLogo = homeOnTop
      ? headerData.team1LogoImage
      : headerData.team2LogoImage;
    const rightLogo = homeOnTop
      ? headerData.team2LogoImage
      : headerData.team1LogoImage;
    const logoSlot = Math.round(
      Math.max(
        36 * 0.82 * scale * vBoost,
        SCOREBOARD_LOGO_SLOT_MIN_PX * vBoost,
      ) * logoSlotScale * rasterCopyExtra,
    );
    const refTitlePx = titleStartPx;
    const pillPadX = Math.max(16, 16 * scale);
    context.save();
    context.textAlign = "left";
    context.font = `700 ${refTitlePx}px ${CANVAS_JERSEY_FONT_STACK}`;
    const reserveTitleStr = "M".repeat(SCOREBOARD_MOMENT_TITLE_RESERVE_CHARS);
    const minCenterW = Math.min(
      innerW * 0.5,
      context.measureText(reserveTitleStr).width + pillPadX,
    );
    context.restore();

    const scoreStartPx = Math.min(
      Math.floor(innerH * 0.72),
      Math.max(12, scaleOverlayPx(overlayTypo.scorePx, titleStartPx)),
    );
    let scoreFont = scoreStartPx;
    const scoreFloorPx = Math.max(
      SCOREBOARD_SCORE_FONT_MIN_PX_UI,
      Math.round(scoreStartPx * 0.85),
    );
    context.textAlign = "left";
    context.textBaseline = "middle";
    context.font = `700 ${scoreFont}px ${CANVAS_JERSEY_FONT_STACK}`;
    if (!isRasterExport) {
      while (scoreFont > scoreFloorPx) {
        const wL = context.measureText(leftScore).width;
        const wR = context.measureText(rightScore).width;
        const skewWL = Math.min(
          Math.max(wL + 30 * scale + innerW * 0.028, innerW * 0.14),
          innerW * 0.52,
        );
        const skewWR = Math.min(
          Math.max(wR + 30 * scale + innerW * 0.028, innerW * 0.14),
          innerW * 0.52,
        );
        const centerW = innerW - skewWL - skewWR - 2 * logoSlot - 2 * dividerLane - 4 * gap;
        if (centerW >= innerW * 0.16 && centerW >= minCenterW) break;
        scoreFont -= 1;
        context.font = `700 ${scoreFont}px ${CANVAS_JERSEY_FONT_STACK}`;
      }
    }
    scoreFont = Math.max(
      Math.min(scoreFont, Math.floor(innerH * 0.7)),
      Math.min(scoreFloorPx, Math.floor(innerH * 0.68)),
    );
    const scoreFontCss = `700 ${scoreFont}px ${CANVAS_JERSEY_FONT_STACK}`;
    context.font = scoreFontCss;
    const stadiumFontPx = computeStadiumExportFontPx(
      innerH,
      titleStartPx,
      overlayTypo.scorePx,
      isRasterExport,
      scaleOverlayPx,
      overlayTypo.stadiumPx,
      scoreFont,
      stadiumBoost,
    );
    const wL = context.measureText(leftScore).width;
    const wR = context.measureText(rightScore).width;
    const skewWL = Math.min(
      Math.max(wL + 30 * scale + innerW * 0.028, innerW * 0.14),
      innerW * 0.52,
    );
    const skewWR = Math.min(
      Math.max(wR + 30 * scale + innerW * 0.028, innerW * 0.14),
      innerW * 0.52,
    );
    const maxCenterW = innerW - skewWL - skewWR - 2 * logoSlot - 2 * dividerLane - 4 * gap;
    const centerW = Math.max(innerW * 0.16, maxCenterW);

    const scoreSkewRatio = SCOREBOARD_TRAP_SKEW_FRAC;
    const skewL = skewWL * scoreSkewRatio;
    const skewR = skewWR * scoreSkewRatio;
    const lineW = Math.max(0.65, Math.min(1.75, SCOREBOARD_DIVIDER_THICKNESS_CQH_FRAC * innerH));

    let cursorX = innerLeft;
    fillTrapezoidScorePanel(context, cursorX, innerTop, skewWL, innerH, skewL, leftP, "leading");
    strokeSlantDividerLine(context, cursorX, innerTop, skewWL, innerH, skewL, "leading", lineW, leftS, shapeDividerGapPx);
    if (showScoreNumbers) {
      context.fillStyle = COLORS_PARTIAL.WHITE;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = scoreFontCss;
      context.fillText(leftScore, cursorX + skewWL / 2 - skewL / 4, midY);
    }
    cursorX += skewWL + dividerLane + gap;

    if (!skipLogos) {
      drawLogoInSlot(leftLogo, cursorX, logoSlot);
    }
    cursorX += logoSlot + gap;

    drawCenterCopyBlock(cursorX, centerW, stadiumFontPx, scoreFont);
    cursorX += centerW + gap;

    if (!skipLogos) {
      drawLogoInSlot(rightLogo, cursorX, logoSlot);
    }
    cursorX += logoSlot + gap + dividerLane;

    fillTrapezoidScorePanel(context, cursorX, innerTop, skewWR, innerH, skewR, rightP, "trailing");
    strokeSlantDividerLine(context, cursorX, innerTop, skewWR, innerH, skewR, "trailing", lineW, rightS, shapeDividerGapPx);
    if (showScoreNumbers) {
      context.fillStyle = COLORS_PARTIAL.WHITE;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = scoreFontCss;
      context.fillText(rightScore, cursorX + skewWR / 2 + skewR / 4, midY);
    }
  } else if (hasCenterCopy) {
    const blockW = innerW * 0.9;
    const blockLeft = innerLeft + (innerW - blockW) / 2;
    const captionScoreRef = isRasterExport
      ? Math.min(
        Math.floor(innerH * 0.72),
        Math.max(12, scaleOverlayPx(overlayTypo.scorePx, titleStartPx)),
      )
      : undefined;
    drawCenterCopyBlock(blockLeft, blockW, undefined, captionScoreRef);
  }
};

export interface LogosPositions {
  visible?: boolean;
  x1?: number;
  y1?: number;
  w1?: number;
  h1?: number;
  x2?: number;
  y2?: number;
  w2?: number;
  h2?: number;
}

export const getScoreAreaWidth = (
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  headerData: MomentHeaderData,
  scaleToImageSize?: boolean,
) : LogosPositions => {
  const IMAGE_WIDTH_PART = 0.8;
  const scale = !scaleToImageSize ? 1 : (canvasWidth * IMAGE_WIDTH_PART) / 300;

  const { maxWidth } = headerData;
  const headerWidth = maxWidth && canvasWidth > maxWidth ? maxWidth : canvasWidth;

  if (headerData.moment.team1Score === null || headerData.moment.team2Score === null) {
    return {
      visible: false,
    };
  }

  const logoSize = 30 * scale;
  const padding = 5 * scale; // Padding to extend a little bit past the logos

  context.fillStyle = COLORS_PARTIAL.BLACK;
  const scoreFontSize = 16 * scale * 1.1; // Increased font size by 10%
  context.font = `600 ${scoreFontSize}px ${FontFamilyTypeEnum_PARTIAL.SFPRO}`;

  const team1Score = `${headerData.moment.team1Score}`;
  const team2Score = `${headerData.moment.team2Score}`;
  const scoreText = `${team1Score} - ${team2Score}`;
  const measurementsScore = context.measureText(scoreText);
  const scoreTextWidth = measurementsScore.width;

  const totalWidth = scoreTextWidth + logoSize * 2 + padding * 4;
  const startX = canvasWidth - totalWidth - padding; // Start from the right side

  const team1LogoX = startX;
  const scoreX = team1LogoX + logoSize + padding;
  const team2LogoX = scoreX + scoreTextWidth + padding;

  const whiteBackgroundY = 0;
  const topBoxHeight = 33 * scale;
  const whiteBackgroundHeight = topBoxHeight;
  const verticalCenter = whiteBackgroundHeight / 2;

  return {
    visible: true,
    x1: team1LogoX,
    y1: verticalCenter - logoSize / 2,
    w1: logoSize,
    h1: logoSize,
    x2: team2LogoX,
    y2: verticalCenter - logoSize / 2,
    w2: logoSize,
    h2: logoSize,
  };
};

export interface DownloadData extends ImageSize, ImageCoordinates {
  link: string;
  name: string;
  filter?: EditorFilter;
  customAdjustments?: Partial<CustomAdjustments>;
}

export const convertToBase64 = (
  downloadData: DownloadData,
  sponsorImage?: HTMLImageElement,
  headerData?: MomentHeaderData,
  blurImage?: boolean,
  imageFilter?: EditorFilter,
): Promise<string> | null => {
  const { x, y, height, width, link, filter: dataFilter, customAdjustments } = downloadData as DownloadData;
  const filter = imageFilter ?? dataFilter;

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = link;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    console.log("No memory for canvas");
    return null;
  }

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const exportLoadedImage = async (): Promise<void> => {
        const natW = img.naturalWidth || 0;
        const natH = img.naturalHeight || 0;
        if (natW === 0 || natH === 0) {
          throw new Error("Image failed to load or has no dimensions");
        }

        try {
          await img.decode?.();
        } catch {
          /* decode() is best-effort — older WebKit may omit or reject */
        }

        const drawWidth = (width && width > 0) ? width : natW;
        const drawHeight = (height && height > 0) ? height : natH;
        const drawX = (x != null && width && width > 0) ? x : 0;
        const drawY = (y != null && height && height > 0) ? y : 0;

        const baseWidth = (drawWidth && drawWidth > 0)
          ? (headerData && drawWidth < headerData.minWidth ? headerData.minWidth : drawWidth)
          : natW;
        let effectiveCanvasWidth = baseWidth;
        let effectiveCanvasHeight = (drawHeight && drawHeight > 0) ? drawHeight : natH;

        const minExportWidth = minMomentCanvasExportWidth(!!headerData);
        // Upscale small crops so sponsor/header artwork is not microscopic (especially on phones).
        if ((sponsorImage || headerData) && baseWidth < minExportWidth) {
          const scaleUp = minExportWidth / baseWidth;
          effectiveCanvasWidth = minExportWidth;
          effectiveCanvasHeight = Math.round(drawHeight * scaleUp);
        }

        canvas.width = effectiveCanvasWidth;
        canvas.height = effectiveCanvasHeight;

        const scale = effectiveCanvasWidth / baseWidth;
        const scaledDrawWidth = drawWidth * scale;
        const scaledDrawHeight = drawHeight * scale;
        const destX = (effectiveCanvasWidth - scaledDrawWidth) / 2;
        const destY = (effectiveCanvasHeight - scaledDrawHeight) / 2;

        // Apply image filter (grayscale, sepia, etc.) and custom adjustments (brightness, contrast, saturation)
        const filterCss = buildImageFilterCss(filter, customAdjustments);

        // Draw the image first (full canvas)
        if (blurImage) {
          context.filter = filterCss !== "none" ? `${filterCss} blur(5px)` : "blur(5px)";
          context.drawImage(
            img,
            drawX,
            drawY,
            drawWidth,
            drawHeight,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
          context.filter = filterCss !== "none" ? filterCss : "none";
          context.drawImage(
            img,
            drawX,
            drawY + drawHeight * 0.25,
            drawWidth,
            drawHeight * 0.5,
            destX,
            destY + scaledDrawHeight * 0.25,
            scaledDrawWidth,
            scaledDrawHeight * 0.5,
          );
        } else {
          if (filterCss !== "none") context.filter = filterCss;
          context.drawImage(
            img,
            drawX,
            drawY,
            drawWidth,
            drawHeight,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
        }
        context.filter = "none";

        if (sponsorImage) {
          renderSponsorOverlay(
            context,
            sponsorImage,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
        }

        // Mobile WebKit often loses short font races — load weights used by the header before toDataURL().
        const finalizeExport = (): void => {
          try {
            if (headerData) {
              renderMomentHeader(
                context,
                effectiveCanvasWidth,
                headerData,
                true,
                false,
                SCOREBOARD_RASTER_VERTICAL_BOOST,
                SCOREBOARD_LOGO_DOWNLOAD_MULT,
                momentHeaderDownloadScoreBoost(true),
              );
            }
            const dataUrl = canvas.toDataURL("image/png");
            releaseCanvas(canvas);
            resolve(dataUrl);
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        };

        if (headerData && typeof document !== "undefined" && document.fonts?.load) {
          try {
            await preloadMomentHeaderFonts(effectiveCanvasWidth, SCOREBOARD_RASTER_VERTICAL_BOOST);
          } catch {
            /* ignore — proceed with system fallback */
          }
        } else if (typeof document !== "undefined" && document.fonts?.ready) {
          await Promise.race([
            document.fonts.ready,
            new Promise<void>((r) => window.setTimeout(r, 900)),
          ]);
        }

        finalizeExport();
      };

      exportLoadedImage().catch((err) => {
        reject(err instanceof Error ? err : new Error(String(err)));
      });
    };
    img.onerror = reject;
  });
};

export const originalConvertToBase64 = (
  downloadData: DownloadData,
  sponsorImage?: HTMLImageElement,
  headerData?: MomentHeaderData,
  blurImage?: boolean,
): Promise<string> | null => {
  const { x, y, height, width, link } = downloadData;

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = link;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    console.log("No memory for canvas");
    return null;
  }

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const runOriginalMomentExport = async (): Promise<void> => {
        const natW = img.naturalWidth || 0;
        const natH = img.naturalHeight || 0;
        if (natW === 0 || natH === 0) {
          throw new Error("Image failed to load or has no dimensions");
        }

        try {
          await img.decode?.();
        } catch {
          /* decode() is best-effort — older WebKit may omit or reject */
        }

        const drawWidth = (width && width > 0) ? width : natW;
        const drawHeight = (height && height > 0) ? height : natH;
        const drawX = (x != null && width && width > 0) ? x : 0;
        const drawY = (y != null && height && height > 0) ? y : 0;

        const baseWidth = (drawWidth && drawWidth > 0)
          ? (headerData && drawWidth < headerData.minWidth ? headerData.minWidth : drawWidth)
          : natW;
        let effectiveCanvasWidth = baseWidth;
        let effectiveCanvasHeight = (drawHeight && drawHeight > 0) ? drawHeight : natH;

        const minExportWidth = minMomentCanvasExportWidth(!!headerData);
        if ((sponsorImage || headerData) && baseWidth < minExportWidth) {
          const scaleUp = minExportWidth / baseWidth;
          effectiveCanvasWidth = minExportWidth;
          effectiveCanvasHeight = Math.round(effectiveCanvasHeight * scaleUp);
        }

        canvas.width = effectiveCanvasWidth;
        canvas.height = effectiveCanvasHeight;

        const scaledDrawWidth = drawWidth * (effectiveCanvasWidth / baseWidth);
        const scaledDrawHeight = drawHeight * (effectiveCanvasWidth / baseWidth);
        const destX = (effectiveCanvasWidth - scaledDrawWidth) / 2;
        const destY = (effectiveCanvasHeight - scaledDrawHeight) / 2;

        if (blurImage) {
          context.filter = "blur(5px)";
          context.drawImage(
            img,
            drawX,
            drawY,
            drawWidth,
            drawHeight,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
          context.filter = "none";
          context.drawImage(
            img,
            drawX,
            drawY + drawHeight * 0.25,
            drawWidth,
            drawHeight * 0.5,
            destX,
            destY + scaledDrawHeight * 0.25,
            scaledDrawWidth,
            scaledDrawHeight * 0.5,
          );
        } else {
          context.drawImage(
            img,
            drawX,
            drawY,
            drawWidth,
            drawHeight,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
        }

        context.filter = "none";

        if (sponsorImage) {
          renderSponsorOverlay(
            context,
            sponsorImage,
            destX,
            destY,
            scaledDrawWidth,
            scaledDrawHeight,
          );
        }

        const finalizeExport = (): void => {
          if (headerData) {
            renderMomentHeader(
              context,
              effectiveCanvasWidth,
              headerData,
              true,
              false,
              SCOREBOARD_RASTER_VERTICAL_BOOST,
              SCOREBOARD_LOGO_DOWNLOAD_MULT,
              momentHeaderDownloadScoreBoost(true),
            );
          }
          const dataUrl = canvas.toDataURL("image/png");
          releaseCanvas(canvas);
          resolve(dataUrl);
        };

        if (headerData && typeof document !== "undefined" && document.fonts?.load) {
          try {
            await preloadMomentHeaderFonts(effectiveCanvasWidth, SCOREBOARD_RASTER_VERTICAL_BOOST);
          } catch {
            /* ignore — proceed with system fallback */
          }
        } else if (typeof document !== "undefined" && document.fonts?.ready) {
          await Promise.race([
            document.fonts.ready,
            new Promise<void>((r) => window.setTimeout(r, 900)),
          ]);
        }

        finalizeExport();
      };

      runOriginalMomentExport().catch((err) => {
        reject(err instanceof Error ? err : new Error(String(err)));
      });
    };
    img.onerror = reject;
  });
};

export const urltoFile = (
  url: string, filename: string, mimeType: string, withDatePrefix = true,
): Promise<File> => {
  const date = new Date();

  const filePrefix = `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}-${(`0${date.getHours()}`).slice(-2)}-${(`0${date.getMinutes()}`).slice(-2)}-${(`0${date.getSeconds()}`).slice(-2)}`;

  const fileNameWithPrefix = withDatePrefix ? `${filePrefix}-${filename}` : filename;

  return fetch(url)
    .then((res) => {
      return res.arrayBuffer();
    })
    .then((buf) => {
      return new File([buf], fileNameWithPrefix, { type: mimeType });
    });
};

// upload any image to S3
export const uploadToS3ServerLargeImage = async (
  image: File,
  name: string,
  mimeType: string,
  s3Folder?: string,
): Promise<string> => {
  // this endpoint should return url to upload image
  const response = await prepareUploadLargeMOTGImage(name, mimeType, s3Folder);
  if (!response.success || !response.uploadURL || !response.imageShortUrl) {
    return "";
  }

  // upload image
  const result = await axios.request({
    url: response.uploadURL,
    method: "PUT",
    data: image,
    headers: { "Content-Type": mimeType },
    onUploadProgress: (p) => {
      // empty for now. can be used to indicate progress
    },
  });

  return response.imageShortUrl;
};

export interface ImageLoadResult {
  image: HTMLImageElement | null;
  dataURL: string | null;
}

/** Cache-bust query for retrying flaky CDN / image loads (safe for absolute URLs). */
export const appendUrlCacheBust = (url: string, bust: number): string => {
  if (!url?.trim() || bust <= 0) return url;
  try {
    const u = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url, typeof window !== "undefined" ? window.location.href : "https://local.invalid");
    u.searchParams.set("_mcb", String(bust));
    return u.toString();
  } catch {
    const joiner = url.includes("?") ? "&" : "?";
    return `${url}${joiner}_mcb=${bust}`;
  }
};

/**
 * Load a remote image into an HTMLImageElement (optional data URL).
 * Retries transient failures; optional decode for reliable drawImage on canvas.
 */
export const loadImage = (
  src: string,
  getDataURL = true,
  maxAttempts = 1,
): Promise<ImageLoadResult> => {
  const trimmed = src?.trim() ?? "";
  if (!trimmed) {
    return Promise.resolve({ image: null, dataURL: null });
  }

  const resolvedAttempts = Math.max(1, maxAttempts);

  const tryOnce = (attemptSrc: string): Promise<ImageLoadResult> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";

      image.onload = () => {
        const finalize = async (): Promise<void> => {
          try {
            if (image.decode) {
              await image.decode();
            }
          } catch {
            /* decode is best-effort */
          }

          if (!getDataURL) {
            resolve({ image, dataURL: null });
            return;
          }

          let outW = image.width;
          let outH = image.height;
          const maxSide = Math.max(outW, outH);
          if (maxSide > MAX_DATAURL_IMAGE_PX) {
            const scale = MAX_DATAURL_IMAGE_PX / maxSide;
            outW = Math.max(1, Math.round(outW * scale));
            outH = Math.max(1, Math.round(outH * scale));
          }

          const canvas = document.createElement("canvas");
          canvas.width = outW;
          canvas.height = outH;
          const context = canvas.getContext("2d");

          if (!context) {
            console.log("No memory for canvas");
          }

          if (context && outW && outH) {
            context.drawImage(image, 0, 0, outW, outH);
          }
          const dataURL = canvas.toDataURL("image/jpg");

          releaseCanvas(canvas);

          resolve({ image, dataURL });
        };
        finalize().catch(reject);
      };

      image.onerror = () => {
        reject(new Error("Image load failed"));
      };

      image.src = attemptSrc;
    });
  };

  const attemptLoad = async (attempt: number, lastError: unknown): Promise<ImageLoadResult> => {
    if (attempt > resolvedAttempts) {
      throw lastError instanceof Error ? lastError : new Error(String(lastError));
    }
    if (attempt > 1) {
      await new Promise<void>((r) => {
        window.setTimeout(r, 140 * (attempt - 1));
      });
    }
    const attemptSrc = attempt === 1 ? trimmed : appendUrlCacheBust(trimmed, attempt - 1);
    try {
      return await tryOnce(attemptSrc);
    } catch (e) {
      return attemptLoad(attempt + 1, e);
    }
  };

  return attemptLoad(1, new Error("Image load failed"));
};

/**
   * Render moment header over image
   * @param context - canvas context to render on
   * @param canvasWidth - area width
   * @param headerData - header information
   * @param scaleToImageSize - scale header to image proportions (share / download)
   */
export const renderMomentHeaderOldVersion = (
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  headerData: MomentHeaderData,
  scaleToImageSize?: boolean,
): void => {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  // we can scale entire image
  // scale downloaded / shared images to the image proportions
  const IMAGE_WIDTH_PART = 0.8;
  const scale = !scaleToImageSize ? 1 : (canvasWidth * IMAGE_WIDTH_PART) / 300;

  // limit width for exporting image
  const { maxWidth } = headerData;
  const headerWidth = maxWidth && canvasWidth > maxWidth ? maxWidth : canvasWidth;
  const topBoxLeftX = (canvasWidth - headerWidth) / 2;
  const middleX = headerWidth / 2 + topBoxLeftX;

  // render top box for the moment text
  const topBoxHeight = 30 * scale;
  context.fillStyle = headerData.team1Color || COLORS_PARTIAL.GREEN_DARK;
  context.strokeStyle = headerData.team1Color || COLORS_PARTIAL.GREEN_DARK;
  context.beginPath();
  context.rect(0, 0, canvasWidth, topBoxHeight);
  context.fill();
  context.closePath();

  // render moment text in the top box
  if (headerData.moment.text) {
    context.beginPath();

    context.fillStyle = COLORS_PARTIAL.WHITE;
    context.font = `700 ${20 * scale}px ${FontFamilyTypeEnum_PARTIAL.Copperplate}`;
    let measurementsFirstLine = context.measureText(headerData.moment.text);
    let textWidth = measurementsFirstLine.width;
    // reduce font to place long description
    if (textWidth > canvasWidth) {
      const fontScale = canvasWidth / textWidth;
      let newFontSize = 20 * scale * fontScale * 0.95;
      if (newFontSize < 8) {
        newFontSize = 8;
      }

      context.font = `700 ${newFontSize}px ${FontFamilyTypeEnum_PARTIAL.Copperplate}`;
      measurementsFirstLine = context.measureText(headerData.moment.text);
      textWidth = measurementsFirstLine.width;
    }

    context.fillText(headerData.moment.text, canvasWidth / 2 - textWidth / 2, 22 * scale);

    context.closePath();
  }

  // render only header for "Other" sport or if we should skip scoreboard
  const sport = headerData.selectedGameData?.sportName;
  const isOtherSport = sport && sport.startsWith("Other");
  if (isOtherSport || headerData.hideScoreboard) {
    return;
  }

  const width = headerWidth * scale < 300 ? headerWidth * scale : 300;
  const coef = (width * scale) / 300;

  // render header box
  context.fillStyle = COLORS_PARTIAL.BLACK;
  context.strokeStyle = COLORS_PARTIAL.BLACK;
  context.beginPath();
  context.rect(middleX - 150 * coef, topBoxHeight, 300 * coef, 45 * coef);
  context.fill();
  context.closePath();

  // render bottom line
  context.beginPath();

  context.fillStyle = COLORS_PARTIAL.WHITE;
  const dateText = headerData.selectedGameData
    ? formatTicketGameDate(headerData.selectedGameData.startDate)
    : "";
  context.font = `700 ${9 * coef}px ${FontFamilyTypeEnum_PARTIAL.POPPINS}`;
  context.fillText(dateText, middleX - 150 * coef + 3 * scale, topBoxHeight + 40 * coef);

  const stadiumText = headerData.selectedGameData ? headerData.selectedGameData.stadium : "";
  context.font = `700 ${10 * coef}px ${FontFamilyTypeEnum_PARTIAL.POPPINS}`;
  const measurementsStadiumText = context.measureText(stadiumText);
  const stadiumTextWidth = measurementsStadiumText.width;
  context.fillText(stadiumText, middleX - stadiumTextWidth / 2, topBoxHeight + 40 * coef);

  context.closePath();

  const logoCoef = headerData.logoImage.width / headerData.logoImage.height;
  const logoWidth = 50 * coef;
  context.drawImage(headerData.logoImage, middleX + 95 * coef,
    topBoxHeight + 30 * coef, logoWidth, logoWidth / logoCoef);

  // render period information and time
  context.beginPath();
  context.fillStyle = COLORS_PARTIAL.WHITE;
  context.font = `700 ${12 * coef}px ${FontFamilyTypeEnum_PARTIAL.POPPINS}`;
  if (headerData.moment.momentPeriodText || headerData.moment.momentPeriodType) {
    const periodText = `${headerData.moment.momentPeriodText || ""} ${headerData.moment.momentPeriodType || ""}`;
    const measurementsPeriodText = context.measureText(periodText);
    const periodTextWidth = measurementsPeriodText.width;
    context.fillText(periodText, middleX - periodTextWidth / 2, topBoxHeight + 13 * coef);
  }

  const momentTime = headerData.moment.momentTime || "";
  if (momentTime) {
    const measurementsMomentTime = context.measureText(momentTime);
    const momentTimeWidth = measurementsMomentTime.width;
    context.fillText(momentTime, middleX - momentTimeWidth / 2, topBoxHeight + 28 * coef);
  }

  context.closePath();

  // render team rounded boxes 105 x 30
  renderRoundedBox(context, middleX - 150 * coef + 3 * scale,
    topBoxHeight + 3 * scale,
    105 * coef, 24 * coef, 5 * scale, headerData.team1Color || COLORS_PARTIAL.GREEN_DARK);
  renderRoundedBox(context, middleX + 150 * coef - 108 * coef,
    topBoxHeight + 3 * scale,
    105 * coef, 24 * coef, 5 * scale, headerData.team2Color || COLORS_PARTIAL.GREY_DARK);

  // render scores
  context.beginPath();
  context.fillStyle = COLORS_PARTIAL.WHITE;
  if (headerData.moment.team2Score || headerData.moment.team2Score === 0) {
    const team2Score = `${headerData.moment.team2Score}`;
    context.font = `500 ${25 * coef}px ${FontFamilyTypeEnum_PARTIAL.POPPINS}`;
    const measurementsScore2 = context.measureText(team2Score);
    const textWidthScore2 = measurementsScore2.width;
    context.fillText(team2Score, middleX - 50 * coef - textWidthScore2,
      topBoxHeight + 23 * coef);
  }

  if (headerData.moment.team1Score || headerData.moment.team1Score === 0) {
    const team1Score = `${headerData.moment.team1Score}`;
    context.font = `500 ${25 * coef}px ${FontFamilyTypeEnum_PARTIAL.POPPINS}`;
    const measurementsScore1 = context.measureText(team1Score);
    const textWidthScore1 = measurementsScore1.width;
    context.fillText(team1Score, middleX + 50 * coef, topBoxHeight + 23 * coef);
  }
  context.closePath();

  // render team logo images
  if (headerData.team2LogoImage && headerData.team2LogoImage.width) {
    const x = middleX - 137 * coef;
    context.drawImage(headerData.team2LogoImage, x, topBoxHeight + 3 * coef, 25 * coef, 25 * coef);
  }
  if (headerData.team1LogoImage && headerData.team1LogoImage.width) {
    const x = middleX + 107 * coef;
    context.drawImage(headerData.team1LogoImage, x, topBoxHeight + 3 * coef, 25 * coef, 25 * coef);
  }
};
